/**
 * GDPR consent management for GoVans.ie.
 * -------------------------------------------------------------------
 * Data controller: GoVans.ie (info@govans.ie)
 * Legal basis: Consent (GDPR Art. 6(1)(a)) for non-essential cookies
 *              and analytics; Contract (Art. 6(1)(b)) for quote/booking
 *              contact-form submissions.
 * Cookie law: Irish ePrivacy Regulations 2011 (SI 336/2011), aligned
 *             with EU ePrivacy Directive 2002/58/EC as amended.
 * -------------------------------------------------------------------
 * Consent choices are stored in localStorage under `govans_consent_v1`
 * with a rolling 12-month expiry per DPC (Data Protection Commission)
 * guidance. After 12 months the banner re-appears automatically.
 */

export const CONSENT_STORAGE_KEY = "govans_consent_v1";
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export const DEFAULT_CONSENT = {
  essential: true,      // always on — required for the site to function
  analytics: false,     // PostHog + Google Analytics
  marketing: false,     // Google Ads / gtag conversion
};

/** Read the stored consent record, or null if none / expired. */
export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > CONSENT_TTL_MS) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    return { ...DEFAULT_CONSENT, ...parsed.choices };
  } catch {
    return null;
  }
}

/** Persist a consent choice and update all third-party tools accordingly. */
export function writeConsent(choices) {
  const record = {
    timestamp: Date.now(),
    version: 1,
    choices: { ...DEFAULT_CONSENT, ...choices, essential: true },
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* localStorage disabled — consent still applied to third-party tools below */
  }
  applyConsent(record.choices);
  return record.choices;
}

/** Clear the consent (used for "withdraw all"). */
export function revokeConsent() {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    /* localStorage disabled — nothing to remove */
  }
  applyConsent(DEFAULT_CONSENT);
}

/**
 * Propagate a consent decision to every third-party tool on the page:
 *   - Google Consent Mode v2 (gtag `ad_storage`, `analytics_storage`, etc.)
 *   - PostHog session recording (lazily initialised on first analytics grant)
 */
export function applyConsent(choices) {
  // --- Google Consent Mode v2 -----------------------------------------
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: choices.marketing ? "granted" : "denied",
      ad_user_data: choices.marketing ? "granted" : "denied",
      ad_personalization: choices.marketing ? "granted" : "denied",
      analytics_storage: choices.analytics ? "granted" : "denied",
    });
  }

  // --- PostHog (only load AFTER analytics consent) --------------------
  if (choices.analytics && typeof window !== "undefined" && !window.__posthogInited) {
    window.__posthogInited = true;
    loadPostHog();
  }
}

/** Deferred PostHog loader — only invoked after analytics consent. */
function loadPostHog() {
  try {
    /* eslint-disable */
    !(function (t, e) {
      var o, n, p, r;
      e.__SV ||
        ((window.posthog = e),
        (e._i = []),
        (e.init = function (i, s, a) {
          function g(t, e) {
            var o = e.split(".");
            2 == o.length && ((t = t[o[0]]), (e = o[1])),
              (t[e] = function () {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
              });
          }
          ((p = t.createElement("script")).type = "text/javascript"),
            (p.crossOrigin = "anonymous"),
            (p.async = !0),
            (p.src =
              s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
              "/static/array.js"),
            (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
          var u = e;
          for (
            void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
              u.people = u.people || [],
              u.toString = function (t) {
                var e = "posthog";
                return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
              },
              u.people.toString = function () {
                return u.toString(1) + ".people (stub)";
              },
              o =
                "init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(
                  " "
                ),
              n = 0;
            n < o.length;
            n++
          )
            g(u, o[n]);
          e._i.push([i, s, a]);
        }),
        (e.__SV = 1));
    })(document, window.posthog || []);
    window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs", {
      api_host: "https://us.i.posthog.com",
      person_profiles: "identified_only",
      session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
    });
    /* eslint-enable */
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("PostHog init failed", err);
    }
  }
}

/**
 * Global helper for the "Manage cookies" footer link.
 * The CookieBanner listens for the custom event and re-opens the banner.
 */
export function openConsentSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("govans:open-consent"));
  }
}
